import { useEffect, useMemo, useRef, useState } from "react";
import { api, formatError } from "@/lib/api";
import { parseFile } from "@/lib/parsers";
import { computeRow } from "@/lib/calc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import PlayerAvatar from "@/components/app/PlayerAvatar";
import { useSeason } from "@/lib/SeasonContext";
import { Save, Upload, Plus, Check, CalendarPlus, Trophy, Crown } from "lucide-react";
import RecordShareCard from "@/components/app/RecordShareCard";
import { toast } from "sonner";

const EDITABLE_COLS = [
  { key: "minutes", label: "MIN", w: "w-14" },
  { key: "fg2_made", label: "2FGM", w: "w-14" },
  { key: "fg2_att", label: "2FGA", w: "w-14" },
  { key: "fg3_made", label: "3FGM", w: "w-14" },
  { key: "fg3_att", label: "3FGA", w: "w-14" },
  { key: "ft_made", label: "FTM", w: "w-14" },
  { key: "ft_att", label: "FTA", w: "w-14" },
  { key: "rebounds", label: "REB", w: "w-14" },
  { key: "assists", label: "AST", w: "w-14" },
  { key: "steals", label: "STL", w: "w-14" },
  { key: "blocks", label: "BLK", w: "w-14" },
  { key: "turnovers", label: "TO", w: "w-14" },
];

export default function StatsEntry() {
  const { activeSeason, seasons } = useSeason();
  const [players, setPlayers] = useState([]);
  const [games, setGames] = useState([]);
  const [selectedGameId, setSelectedGameId] = useState("");
  const [rows, setRows] = useState({}); // {player_id: {fields...}}
  const [saved, setSaved] = useState(false);
  const [newRecords, setNewRecords] = useState([]);
  const [saving, setSaving] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [totals, setTotals] = useState(null); // {season, source, totals: [...]}
  const [totalsLoading, setTotalsLoading] = useState(false);
  const fileRef = useRef(null);
  const perGameFileRef = useRef(null);
  const totalsFileRef = useRef(null);
  const inputRefs = useRef({}); // {`${playerIdx}-${colKey}`: ref}

  // Historical = viewing any season that isn't the newest known season.
  // Basketball runs Nov–Mar with no season overlap, so string-sort DESC reliably
  // identifies the current season as seasons[0].
  const isHistoricalSeason = activeSeason && seasons.length > 0 && seasons[0] !== activeSeason;

  useEffect(() => {
    loadInitial();
    setSelectedGameId("");
    setRows({});
  }, [activeSeason]);

  // Load season totals whenever the season changes or after an upload succeeds.
  const loadTotals = async () => {
    if (!activeSeason) return;
    setTotalsLoading(true);
    try {
      const { data } = await api.get("/season-totals", { params: { season: activeSeason } });
      setTotals(data);
    } catch {
      setTotals({ season: activeSeason, source: null, totals: [] });
    } finally {
      setTotalsLoading(false);
    }
  };
  useEffect(() => { loadTotals(); }, [activeSeason]);

  async function loadInitial() {
    const params = activeSeason ? { season: activeSeason } : {};
    const isHistorical = activeSeason && seasons.length > 0 && seasons[0] !== activeSeason;
    const playerParams = isHistorical
      ? { season: activeSeason, include_active: true, status_filter: "active,graduated,inactive" }
      : { status_filter: "active" };
    const [p, g] = await Promise.all([
      api.get("/players", { params: playerParams }),
      api.get("/games", { params }),
    ]);
    setPlayers(p.data);
    setGames(g.data);
  }

  useEffect(() => {
    if (!selectedGameId) return;
    api.get("/game-stats", { params: { game_id: selectedGameId } }).then(({ data }) => {
      const m = {};
      for (const s of data) m[s.player_id] = s;
      setRows(m);
    });
  }, [selectedGameId]);

  const selectedGame = useMemo(() => games.find((g) => g.id === selectedGameId), [games, selectedGameId]);

  const setCell = (pid, key, val) => {
    const clean = val.replace(/[^0-9]/g, "");
    setRows((r) => ({ ...r, [pid]: { ...(r[pid] || {}), [key]: clean === "" ? "" : parseInt(clean, 10) } }));
    setSaved(false);
  };

  const handleTab = (e, playerIdx, colIdx) => {
    if (e.key !== "Tab") return;
    e.preventDefault();
    const dir = e.shiftKey ? -1 : 1;
    let nextCol = colIdx + dir;
    let nextRow = playerIdx;
    if (nextCol >= EDITABLE_COLS.length) {
      nextCol = 0;
      nextRow = playerIdx + 1;
    }
    if (nextCol < 0) {
      nextCol = EDITABLE_COLS.length - 1;
      nextRow = playerIdx - 1;
    }
    if (nextRow < 0 || nextRow >= players.length) return;
    const ref = inputRefs.current[`${nextRow}-${EDITABLE_COLS[nextCol].key}`];
    if (ref) { ref.focus(); ref.select?.(); }
  };

  const save = async () => {
    if (!selectedGameId) {
      toast.error("Select a game first");
      return;
    }
    setSaving(true);
    try {
      const stats = players.map((p) => ({
        player_id: p.id,
        ...EDITABLE_COLS.reduce((acc, c) => {
          acc[c.key] = parseInt(rows[p.id]?.[c.key], 10) || 0;
          return acc;
        }, {}),
      }));
      const { data: saveResp } = await api.post("/game-stats", { game_id: selectedGameId, stats });
      setSaved(true);
      setNewRecords(saveResp?.new_records || []);
      if (saveResp?.new_records?.length) {
        toast.success(`Stats saved · ${saveResp.new_records.length} record${saveResp.new_records.length > 1 ? "s" : ""} on the board! 🏆`);
      } else {
        toast.success("Stats saved!");
      }
      // refresh games to reflect updated scores
      const params = activeSeason ? { season: activeSeason } : {};
      const g = await api.get("/games", { params });
      setGames(g.data);
      loadTotals();
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/games/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Schedule uploaded: ${data.added} new, ${data.skipped_duplicates} duplicates`);
      const params = activeSeason ? { season: activeSeason } : {};
      const g = await api.get("/games", { params });
      setGames(g.data);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      e.target.value = "";
    }
  };

  const handlePerGameUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (activeSeason) fd.append("season", activeSeason);
      const { data } = await api.post("/stats/upload-per-game", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const parts = [
        `${data.stats_saved} stat row${data.stats_saved === 1 ? "" : "s"} saved`,
        data.games_created > 0 && `${data.games_created} game${data.games_created === 1 ? "" : "s"} created`,
        data.games_matched > 0 && `${data.games_matched} matched`,
      ].filter(Boolean).join(" · ");
      if (data.unmatched_players?.length) {
        toast.warning(`${parts}. Unmatched: ${data.unmatched_players.slice(0, 5).join(", ")}${data.unmatched_players.length > 5 ? "…" : ""}`, { duration: 10000 });
      } else {
        toast.success(`Uploaded to ${data.season} · ${parts}`);
      }
      const params = activeSeason ? { season: activeSeason } : {};
      const g = await api.get("/games", { params });
      setGames(g.data);
      loadTotals();
    } catch (err) { toast.error(formatError(err)); }
    finally { e.target.value = ""; }
  };

  const handleTotalsUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (activeSeason) fd.append("season", activeSeason);
      const { data } = await api.post("/stats/upload-season-totals", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (data.unmatched_players?.length) {
        toast.warning(`${data.stats_saved} season totals saved for ${data.season}. Unmatched: ${data.unmatched_players.slice(0, 5).join(", ")}${data.unmatched_players.length > 5 ? "…" : ""}`, { duration: 10000 });
      } else {
        toast.success(`${data.stats_saved} season totals saved for ${data.season}`);
      }
      loadTotals();
    } catch (err) { toast.error(formatError(err)); }
    finally { e.target.value = ""; }
  };

  return (
    <div className="p-6 md:p-10">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
        <div>
          <p className="eyebrow">Coach Tools</p>
          <h1 className="heading-1">Stats <span className="text-burgundy">Entry</span></h1>
          {activeSeason && (
            <div className="mt-3 inline-flex items-center gap-2 bg-burgundy/15 border border-burgundy/50 rounded-md px-3 py-1.5" data-testid="stats-active-season-pill">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
              <span className="text-[10px] uppercase tracking-[0.2em] text-ink-300 font-bold">Season</span>
              <span className="font-display font-black text-white uppercase tracking-wider text-sm">{activeSeason}</span>
              {isHistoricalSeason && (
                <span className="text-[10px] uppercase tracking-wider text-amber-300 border-l border-burgundy/40 pl-2 ml-1 font-bold">Historical</span>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="outline"
            className="border-ink-700 hover:bg-ink-800"
            onClick={() => fileRef.current?.click()}
            title="Import a game schedule spreadsheet — expects DATE, OPPONENT, H/A columns. Imports to the active season shown at the top."
            data-testid="upload-schedule-btn"
          >
            <Upload className="w-4 h-4 mr-2" /> Upload Schedule
          </Button>
          <input ref={fileRef} type="file" accept=".xlsx,.csv" className="hidden" onChange={handleUpload} />

          <Button
            variant="outline"
            className="border-ink-700 hover:bg-ink-800"
            onClick={() => perGameFileRef.current?.click()}
            title="Bulk import per-game stats. Rows: PLAYER, DATE, OPPONENT, 2FGM, 2FGA, 3FGM, 3FGA, FTM, FTA, TOT REB, TOT AST, STL, BLK, TO. Imports to the season you're currently viewing."
            data-testid="upload-per-game-btn"
          >
            <Upload className="w-4 h-4 mr-2" /> Upload Per-Game Stats
          </Button>
          <input ref={perGameFileRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={handlePerGameUpload} />

          {/* Season Totals upload is only relevant for HISTORICAL seasons — the current
              season's totals are auto-calculated from per-game stats. */}
          {isHistoricalSeason && (
            <>
              <Button
                variant="outline"
                className="border-ink-700 hover:bg-ink-800"
                onClick={() => totalsFileRef.current?.click()}
                title="Bulk import season totals (aggregate — no game-by-game). Rows: PLAYER, GP, 2FGM, 2FGA, 3FGM, 3FGA, FTM, FTA, REB, AST, STL, BLK, TO. Imports to the season you're currently viewing. Best for old seasons where game logs weren't kept."
                data-testid="upload-totals-btn"
              >
                <Upload className="w-4 h-4 mr-2" /> Upload Season Totals
              </Button>
              <input ref={totalsFileRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={handleTotalsUpload} />
            </>
          )}

          <Button
            onClick={() => setPickerOpen(true)}
            className="bg-burgundy hover:bg-burgundy-hover"
            data-testid="new-game-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> New Game
          </Button>
        </div>
      </div>

      {saved && (
        <div className="mb-4 bg-green-500/15 border border-green-500/40 text-green-300 px-4 py-3 rounded-md flex items-center gap-3 flex-wrap" data-testid="save-confirmation-banner">
          <Check className="w-4 h-4" />
          <span className="flex-1">Stats saved successfully.</span>
          {selectedGameId && (
            <a
              href={`/game/${selectedGameId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs uppercase tracking-wider bg-burgundy text-white px-3 py-1.5 rounded hover:bg-burgundy-hover transition"
              data-testid="open-share-card-link"
            >
              View Box Score · Share Card →
            </a>
          )}
        </div>
      )}

      {/* NEW RECORD celebration banner — shown when the saved stats landed in top-3 all-time */}
      {newRecords.length > 0 && (
        <div className="mb-4 relative overflow-hidden rounded-lg border-2 border-burgundy" data-testid="new-record-banner">
          <div className="absolute inset-0 bg-gradient-to-r from-burgundy via-burgundy/90 to-burgundy/70" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,215,0,0.25),transparent_55%)]" />
          <div className="relative px-5 py-4">
            <div className="flex items-center gap-3 mb-3">
              <Trophy className="w-6 h-6 text-amber-300" />
              <div>
                <p className="text-[10px] uppercase tracking-[0.25em] text-amber-200/80 font-bold">Record Book Update</p>
                <p className="font-display text-lg md:text-xl font-black uppercase text-white leading-tight">
                  {newRecords.length === 1 ? "New Top-3 Record!" : `${newRecords.length} New Top-3 Records!`}
                </p>
              </div>
              <button
                onClick={() => setNewRecords([])}
                className="ml-auto text-white/70 hover:text-white text-xs uppercase tracking-wider"
                data-testid="dismiss-new-record"
              >
                Dismiss
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {newRecords.map((nr, i) => (
                <div key={i} className="bg-black/25 rounded px-3 py-2 flex items-center gap-3" data-testid={`new-record-${nr.category}-${i}`}>
                  <div className="w-6 h-6 rounded-full bg-amber-400/90 text-burgundy font-black text-xs flex items-center justify-center shrink-0">
                    #{nr.rank}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-display font-bold uppercase text-white text-sm truncate">{nr.player_name}</p>
                    <p className="text-[10px] uppercase tracking-wider text-amber-100/80 truncate">{nr.label} · vs {nr.opponent}</p>
                  </div>
                  <p className="font-display text-2xl font-black text-amber-300 shrink-0">
                    {nr.value}<span className="text-[10px] text-amber-100/70 font-bold ml-0.5">{nr.suffix}</span>
                  </p>
                  <RecordShareCard record={nr} compact />
                </div>
              ))}
            </div>
            <a
              href="/records"
              className="inline-flex items-center gap-2 mt-3 text-xs uppercase tracking-wider text-amber-200 hover:text-white transition"
              data-testid="view-record-book-link"
            >
              <Crown className="w-3.5 h-3.5" /> View the full Hall of Records →
            </a>
          </div>
        </div>
      )}

      {!isHistoricalSeason && (
        <>
          <div className="bg-ink-800 rounded-lg border border-white/5 p-4 mb-4 flex flex-wrap items-center gap-4">
            <Label className="eyebrow">Game</Label>
            <Select value={selectedGameId} onValueChange={setSelectedGameId}>
              <SelectTrigger className="w-72 bg-ink-900 border-ink-700" data-testid="game-select">
                <SelectValue placeholder="Select a game…" />
              </SelectTrigger>
              <SelectContent className="bg-ink-900 border-ink-700">
                {games.map((g) => (
                  <SelectItem key={g.id} value={g.id}>
                    {g.date} · {g.home_away === "home" ? "vs" : "@"} {g.opponent}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedGame && (
              <div className="text-sm text-ink-300">
                <span className="text-white font-semibold">{selectedGame.home_away === "home" ? "vs" : "@"} {selectedGame.opponent}</span>
                {selectedGame.nphs_score !== null && (
                  <span className="ml-3">{selectedGame.nphs_score} - {selectedGame.opp_score}</span>
                )}
              </div>
            )}
            <Button onClick={save} disabled={saving || !selectedGameId} className="ml-auto bg-burgundy hover:bg-burgundy-hover" data-testid="save-stats-btn">
              <Save className="w-4 h-4 mr-2" /> {saving ? "Saving…" : "Save Stats"}
            </Button>
          </div>

          {/* Editable Stat Table */}
          <div className="bg-ink-800 rounded-lg border border-white/5 overflow-hidden">
            <div className="scroll-table overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-ink-900 text-ink-300">
                  <tr className="text-left">
                    <th className="sticky left-0 bg-ink-900 z-10 px-4 py-3 uppercase text-xs tracking-wider">Player</th>
                    {EDITABLE_COLS.map((c) => (
                      <th key={c.key} className="px-2 py-3 text-center uppercase text-xs tracking-wider">{c.label}</th>
                    ))}
                    <th className="px-2 py-3 text-center uppercase text-xs tracking-wider bg-ink-800/50">2FG%</th>
                    <th className="px-2 py-3 text-center uppercase text-xs tracking-wider bg-ink-800/50">3FG%</th>
                    <th className="px-2 py-3 text-center uppercase text-xs tracking-wider bg-ink-800/50">FT%</th>
                    <th className="px-2 py-3 text-center uppercase text-xs tracking-wider bg-burgundy/40 text-white">PTS</th>
                  </tr>
                </thead>
                <tbody>
                  {players.map((p, pi) => {
                    const row = rows[p.id] || {};
                    const calc = computeRow(row);
                    return (
                      <tr key={p.id} className="border-t border-ink-700/40 hover:bg-ink-900/40">
                        <td className="sticky left-0 bg-ink-800 z-10 px-4 py-2">
                          <div className="flex items-center gap-3 min-w-[180px]">
                            <PlayerAvatar player={p} size="sm" />
                            <div>
                              <p className="font-semibold text-sm leading-tight">{p.player_name}</p>
                              <p className="text-[10px] text-ink-400">#{p.jersey_number || "—"}</p>
                            </div>
                          </div>
                        </td>
                        {EDITABLE_COLS.map((c, ci) => (
                          <td key={c.key} className={`${c.w} stat-cell`}>
                            <input
                              ref={(el) => (inputRefs.current[`${pi}-${c.key}`] = el)}
                              type="text"
                              inputMode="numeric"
                              value={row[c.key] ?? ""}
                              onChange={(e) => setCell(p.id, c.key, e.target.value)}
                              onKeyDown={(e) => handleTab(e, pi, ci)}
                              onFocus={(e) => e.target.select()}
                              className="w-full"
                              data-testid={`stat-${p.slug}-${c.key}`}
                            />
                          </td>
                        ))}
                        <td className="px-2 py-2 text-center text-ink-300 text-xs">{calc.fg2_pct}%</td>
                        <td className="px-2 py-2 text-center text-ink-300 text-xs">{calc.fg3_pct}%</td>
                        <td className="px-2 py-2 text-center text-ink-300 text-xs">{calc.ft_pct}%</td>
                        <td className="px-2 py-2 text-center font-display font-bold text-white bg-burgundy/30">{calc.points}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {!selectedGameId && (
              <p className="text-center text-ink-400 text-sm py-6">Pick a game above to start entering stats.</p>
            )}
          </div>
        </>
      )}

      {/* Season Totals — read-only. Auto-calculated for current season; uploaded for historical. */}
      <SeasonTotalsTable
        totals={totals}
        loading={totalsLoading}
        isHistorical={isHistoricalSeason}
        season={activeSeason}
      />

      {/* Game picker dialog */}
      <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
        <DialogContent className="bg-ink-900 border-ink-700 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="heading-3">Pick a Game</DialogTitle>
            <DialogDescription className="text-ink-300">Select an upcoming or recent game to start entering stats.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {games.length === 0 && (
              <p className="text-ink-400 text-sm">No games yet. Upload your schedule or add one manually.</p>
            )}
            {games.map((g) => (
              <button
                key={g.id}
                onClick={() => { setSelectedGameId(g.id); setPickerOpen(false); }}
                className="w-full text-left px-4 py-3 rounded-md bg-ink-800 hover:bg-burgundy/30 transition flex items-center justify-between"
                data-testid={`game-picker-${g.id}`}
              >
                <span>
                  <span className="text-ink-400 text-xs uppercase tracking-wider mr-2">{g.date}</span>
                  <span className="font-display font-bold uppercase">{g.home_away === "home" ? "vs" : "@"} {g.opponent}</span>
                </span>
                {g.playoff && <span className="text-amber-400 text-xs uppercase">Playoff</span>}
              </button>
            ))}
          </div>
          <div className="border-t border-ink-700 pt-4 mt-2">
            <Button onClick={() => { setPickerOpen(false); setManualOpen(true); }} variant="outline" className="border-ink-700 hover:bg-ink-800 w-full">
              <CalendarPlus className="w-4 h-4 mr-2" /> Game not on schedule — add manually
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <ManualGameDialog
        open={manualOpen}
        onClose={() => setManualOpen(false)}
        onCreated={(g) => {
          setGames((arr) => [...arr, g].sort((a, b) => (a.date || "").localeCompare(b.date || "")));
          setSelectedGameId(g.id);
          setManualOpen(false);
        }}
      />
    </div>
  );
}

function ManualGameDialog({ open, onClose, onCreated }) {
  const [form, setForm] = useState({ date: "", opponent: "", home_away: "home", nphs_score: "", opp_score: "", location: "", notes: "", playoff: false });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form, nphs_score: form.nphs_score ? +form.nphs_score : null, opp_score: form.opp_score ? +form.opp_score : null };
      const { data } = await api.post("/games", payload);
      toast.success("Game created");
      onCreated(data);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-ink-900 border-ink-700 text-white max-w-lg">
        <DialogHeader>
          <DialogTitle className="heading-3">Add Game</DialogTitle>
          <DialogDescription className="text-ink-300">Quick manual entry — for games not loaded from your schedule.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3" data-testid="manual-game-form">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Date" type="date" value={form.date} onChange={(v) => setForm({ ...form, date: v })} required testId="manual-game-date" />
            <Field label="Opponent" value={form.opponent} onChange={(v) => setForm({ ...form, opponent: v })} required testId="manual-game-opponent" />
            <div>
              <Label className="eyebrow">Home / Away</Label>
              <Select value={form.home_away} onValueChange={(v) => setForm({ ...form, home_away: v })}>
                <SelectTrigger className="bg-ink-800 border-ink-700 mt-2"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-ink-900 border-ink-700">
                  <SelectItem value="home">Home</SelectItem>
                  <SelectItem value="away">Away</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Field label="Location" value={form.location} onChange={(v) => setForm({ ...form, location: v })} testId="manual-game-location" />
            <Field label="NPHS Pts" value={form.nphs_score} onChange={(v) => setForm({ ...form, nphs_score: v })} inputMode="numeric" />
            <Field label="Opp Pts" value={form.opp_score} onChange={(v) => setForm({ ...form, opp_score: v })} inputMode="numeric" />
          </div>
          <Field label="Notes" value={form.notes} onChange={(v) => setForm({ ...form, notes: v })} />
          <label className="inline-flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.playoff} onChange={(e) => setForm({ ...form, playoff: e.target.checked })} /> Playoff game
          </label>
          <Button type="submit" disabled={busy} className="w-full bg-burgundy hover:bg-burgundy-hover" data-testid="manual-game-submit">
            {busy ? "Saving…" : "Create Game"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, value, onChange, required, type = "text", inputMode, testId }) {
  return (
    <div>
      <Label className="eyebrow">{label}</Label>
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        type={type}
        inputMode={inputMode}
        className="bg-ink-800 border-ink-700 mt-2"
        data-testid={testId}
      />
    </div>
  );
}

const TOTALS_COLS = [
  { key: "gp",         label: "GP",   cls: "text-white" },
  { key: "fg2_made",   label: "2FGM" },
  { key: "fg2_att",    label: "2FGA" },
  { key: "fg2_pct",    label: "2FG%", pct: true },
  { key: "fg3_made",   label: "3FGM" },
  { key: "fg3_att",    label: "3FGA" },
  { key: "fg3_pct",    label: "3FG%", pct: true },
  { key: "ft_made",    label: "FTM" },
  { key: "ft_att",     label: "FTA" },
  { key: "ft_pct",     label: "FT%",  pct: true },
  { key: "rebounds",   label: "REB" },
  { key: "assists",    label: "AST" },
  { key: "steals",     label: "STL" },
  { key: "blocks",     label: "BLK" },
  { key: "turnovers",  label: "TO" },
  { key: "total_points", label: "PTS", cls: "font-display font-bold text-white bg-burgundy/30" },
];

function SeasonTotalsTable({ totals, loading, isHistorical, season }) {
  const heading = isHistorical ? "Season Totals" : "Season Totals (Live)";
  const subhead = isHistorical
    ? `Uploaded totals for ${season}. Upload a spreadsheet to add or update rows.`
    : `Auto-calculated from every game entered this season. Updates automatically as stats are saved.`;
  const rows = totals?.totals || [];
  const hasUploaded = totals?.source === "uploaded";

  return (
    <div className={`bg-ink-800 rounded-lg border border-white/5 overflow-hidden ${isHistorical ? "" : "mt-6"}`} data-testid="season-totals-panel">
      <div className="px-4 py-3 border-b border-ink-700/50 flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="heading-3 text-white flex items-center gap-2">
            {heading}
            {isHistorical && hasUploaded && (
              <span className="text-[10px] uppercase tracking-wider bg-amber-500/20 text-amber-300 border border-amber-500/40 rounded px-2 py-0.5 font-bold">
                Uploaded
              </span>
            )}
            {!isHistorical && (
              <span className="text-[10px] uppercase tracking-wider bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 rounded px-2 py-0.5 font-bold">
                Auto-calculated
              </span>
            )}
          </h2>
          <p className="text-xs text-ink-400 mt-0.5">{subhead}</p>
        </div>
      </div>
      {loading ? (
        <p className="text-center text-ink-400 text-sm py-8" data-testid="season-totals-loading">Loading totals…</p>
      ) : rows.length === 0 ? (
        <p className="text-center text-ink-400 text-sm py-8" data-testid="season-totals-empty">
          {isHistorical
            ? "No totals uploaded yet for this season. Click \"Upload Season Totals\" above to import a spreadsheet."
            : "No stats saved yet this season. Enter a game above to see totals populate."}
        </p>
      ) : (
        <div className="scroll-table overflow-x-auto">
          <table className="min-w-full text-sm" data-testid="season-totals-table">
            <thead className="bg-ink-900 text-ink-300">
              <tr className="text-left">
                <th className="sticky left-0 bg-ink-900 z-10 px-4 py-3 uppercase text-xs tracking-wider">Player</th>
                {TOTALS_COLS.map((c) => (
                  <th key={c.key} className="px-2 py-3 text-center uppercase text-xs tracking-wider">{c.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.player.id} className="border-t border-ink-700/40 hover:bg-ink-900/40" data-testid={`totals-row-${r.player.slug}`}>
                  <td className="sticky left-0 bg-ink-800 z-10 px-4 py-2">
                    <div className="flex items-center gap-3 min-w-[180px]">
                      <PlayerAvatar player={r.player} size="sm" />
                      <div>
                        <p className="font-semibold text-sm leading-tight">{r.player.player_name}</p>
                        <p className="text-[10px] text-ink-400">#{r.player.jersey_number || "—"}</p>
                      </div>
                    </div>
                  </td>
                  {TOTALS_COLS.map((c) => (
                    <td key={c.key} className={`px-2 py-2 text-center text-sm ${c.cls || "text-ink-200"}`}>
                      {c.pct ? `${r[c.key] ?? 0}%` : (r[c.key] ?? 0)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
